import React, {useState, useEffect, useRef} from 'react';
import {Link} from 'react-router-dom';

export function ProfilePage({
    profileImage = null,
    name = 'Your Name',
    tagline = 'Your tagline goes here',
    currentStatus = '',
    faqItems = [],
    sponsorshipInfo = '',
    email: initialEmail = '',
    onSubscribe = null,
    editable = false,
    onUpdate = null,
    articles = [] // Indexed articles for AI to search
}) {
    const [email, setEmail] = useState('');
    const [subscribed, setSubscribed] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editData, setEditData] = useState({
        name,
        tagline,
        currentStatus,
        sponsorshipInfo,
        email: initialEmail
    });

    // AI Chat state
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [chatMessages, setChatMessages] = useState([]);
    const [chatInput, setChatInput] = useState('');
    const [isAiTyping, setIsAiTyping] = useState(false);
    const chatEndRef = useRef(null);

    // Scroll chat to bottom
    useEffect(() => {
        chatEndRef.current?.scrollIntoView({behavior: 'smooth'});
    }, [chatMessages]);

    const handleSubscribe = (e) => {
        e.preventDefault();
        if (email && onSubscribe) {
            onSubscribe(email);
        }
        setSubscribed(true);
        setTimeout(() => setSubscribed(false), 3000);
    };

    const handleSaveProfile = () => {
        if (onUpdate) {
            onUpdate(editData);
        }
        localStorage.setItem('koenig-profile-data', JSON.stringify(editData));
        setIsEditing(false);
    };

    // AI Chat functionality
    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!chatInput.trim() || isAiTyping) return;

        const userMessage = {
            role: 'user',
            content: chatInput.trim(),
            timestamp: new Date()
        };

        setChatMessages(prev => [...prev, userMessage]);
        setChatInput('');
        setIsAiTyping(true);

        // Simulate AI response based on indexed articles
        setTimeout(() => {
            const aiResponse = generateAIResponse(chatInput, articles, editData.name);
            setChatMessages(prev => [...prev, {
                role: 'assistant',
                content: aiResponse.content,
                articleLink: aiResponse.articleLink,
                timestamp: new Date()
            }]);
            setIsAiTyping(false);
        }, 1500);
    };

    // Generate AI response based on user query and articles
    const generateAIResponse = (query, indexedArticles, authorName) => {
        const queryLower = query.toLowerCase();

        // Get all articles from localStorage
        const allArticlesStr = localStorage.getItem('koenig-articles');
        const allArticles = allArticlesStr ? JSON.parse(allArticlesStr) : [];

        // Search through articles for relevant content
        let relevantArticle = null;
        let responseContent = '';

        // Check for keywords in articles
        for (const article of allArticles) {
            const titleLower = (article.title || '').toLowerCase();
            const contentLower = (article.content || '').toLowerCase();

            if (titleLower.includes(queryLower) || contentLower.includes(queryLower)) {
                relevantArticle = article;
                break;
            }
        }

        // Generate contextual response
        if (queryLower.includes('stokes') || queryLower.includes('captain')) {
            responseContent = `In his analysis from 'The Lord's Long Room' (Aug 14), ${authorName.split(' ')[0]} argued that Stokes was too aggressive. He mentioned that the field placements were 'innovative but risky.'`;
            relevantArticle = allArticles.find(a => a.title?.toLowerCase().includes('stokes') || a.title?.toLowerCase().includes('lord'));
        } else if (queryLower.includes('ipl')) {
            responseContent = `Yes, ${authorName.split(' ')[0]} covers the IPL, but only from a tactical view, not gossip. Check out his analysis pieces for in-depth tactical breakdowns.`;
        } else if (queryLower.includes('ashes')) {
            responseContent = `${authorName.split(' ')[0]} has been covering the Ashes Test extensively. His latest piece focuses on Day 3 and Stokes' captaincy decisions.`;
            relevantArticle = allArticles.find(a => a.title?.toLowerCase().includes('ashes'));
        } else if (queryLower.includes('sponsor') || queryLower.includes('advertis')) {
            responseContent = `For sponsorship inquiries, ${authorName.split(' ')[0]} charges $500 per slot. The audience is 80% UK/India. Email at ${editData.email || 'alex@cricket.com'} for details.`;
        } else if (allArticles.length > 0) {
            // Default response with recent article
            relevantArticle = allArticles[0];
            responseContent = `Based on ${authorName.split(' ')[0]}'s recent articles, I found some relevant insights. Check out "${relevantArticle.title}" for more details.`;
        } else {
            responseContent = `I'm ${authorName.split(' ')[0]}'s AI assistant. I can help you find information from their articles and content. What would you like to know?`;
        }

        return {
            content: responseContent,
            articleLink: relevantArticle ? {
                title: relevantArticle.title,
                url: `/?articleId=${relevantArticle.id}`
            } : null
        };
    };

    const formatTime = (date) => {
        return date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    };

    const displayName = editData.name || name;
    const firstName = displayName.split(' ')[0].split('(')[0].trim();

    return (
        <div style={containerStyle}>
            {/* Top Navigation Bar */}
            <div style={navBarStyle}>
                <div style={logoStyle}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="#FF6719">
                        <rect x="2" y="2" width="20" height="6" rx="1"/>
                        <rect x="2" y="10" width="20" height="4" rx="1"/>
                        <rect x="2" y="16" width="20" height="6" rx="1"/>
                    </svg>
                </div>
                <div style={navButtonsStyle}>
                    {editable && (
                        <Link to="/linktree/edit" style={editButtonLinkStyle}>
                            Edit Profile
                        </Link>
                    )}
                    <button style={navButtonStyle}>Log in</button>
                    <button style={signUpButtonStyle}>Sign up</button>
                </div>
            </div>

            {/* Profile Header */}
            <div style={headerSectionStyle}>
                <div style={profileImageContainerStyle}>
                    {profileImage ? (
                        <img src={profileImage} alt={name} style={profileImageStyle} />
                    ) : (
                        <div style={defaultAvatarStyle}>
                            <svg width="48" height="48" viewBox="0 0 32 32">
                                <defs>
                                    <linearGradient id="profile-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                                        <stop offset="0%" style={{stopColor: '#667EEA'}} />
                                        <stop offset="100%" style={{stopColor: '#764BA2'}} />
                                    </linearGradient>
                                </defs>
                                <rect width="32" height="32" rx="16" fill="url(#profile-grad)"/>
                                <circle cx="16" cy="12" r="5" fill="#FFFFFF" opacity="0.9"/>
                                <path d="M16 19c-5 0-9 2.5-9 5.5v1.5h18v-1.5c0-3-4-5.5-9-5.5z" fill="#FFFFFF" opacity="0.9"/>
                            </svg>
                        </div>
                    )}
                </div>

                {isEditing ? (
                    <div style={{marginBottom: '16px'}}>
                        <input
                            type="text"
                            value={editData.name}
                            onChange={(e) => setEditData({...editData, name: e.target.value})}
                            style={editInputStyle}
                            placeholder="Your name or headline"
                        />
                    </div>
                ) : (
                    <h1 style={nameStyle}>{displayName}</h1>
                )}

                {isEditing ? (
                    <input
                        type="text"
                        value={editData.tagline}
                        onChange={(e) => setEditData({...editData, tagline: e.target.value})}
                        style={{...editInputStyle, fontSize: '14px'}}
                        placeholder="Your tagline"
                    />
                ) : (
                    tagline && <p style={taglineStyle}>{editData.tagline || tagline}</p>
                )}
            </div>

            {/* Email Subscription Box */}
            <div style={subscriptionBoxStyle}>
                <div style={brandingStyle}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="#FF6719">
                        <rect x="2" y="2" width="20" height="6" rx="1"/>
                        <rect x="2" y="10" width="20" height="4" rx="1"/>
                        <rect x="2" y="16" width="20" height="6" rx="1"/>
                    </svg>
                    <span style={{fontWeight: 600, color: '#1f2937'}}>Substack</span>
                </div>
                <form onSubmit={handleSubscribe} style={subscriptionFormStyle}>
                    <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter your email here..."
                        style={emailInputStyle}
                        disabled={subscribed}
                    />
                    <button
                        type="submit"
                        style={subscribeButtonStyle(subscribed)}
                        disabled={subscribed || !email}
                    >
                        {subscribed ? 'Subscribed!' : 'Sign it up'}
                    </button>
                </form>
            </div>

            {/* Current Status */}
            <div style={statusBoxStyle}>
                <span style={{marginRight: '8px'}}>✍️</span>
                {isEditing ? (
                    <input
                        type="text"
                        value={editData.currentStatus}
                        onChange={(e) => setEditData({...editData, currentStatus: e.target.value})}
                        style={{...editInputStyle, flex: 1, fontSize: '14px', textAlign: 'left'}}
                        placeholder="What are you working on?"
                    />
                ) : (
                    <span dangerouslySetInnerHTML={{__html: editData.currentStatus || currentStatus || 'Update your current status...'}} />
                )}
            </div>

            {/* FAQ & Sponsorships */}
            <div style={faqSectionStyle}>
                <h2 style={sectionTitleStyle}>FAQ & Sponsorships</h2>

                {faqItems.map((item, idx) => (
                    <div key={idx} style={faqItemStyle}>
                        <p style={faqQuestionStyle}>{item.question}</p>
                        <p style={faqAnswerStyle}>{item.answer}</p>
                    </div>
                ))}

                <div style={sponsorshipStyle}>
                    <strong>Sponsorships: </strong>
                    {isEditing ? (
                        <input
                            type="text"
                            value={editData.sponsorshipInfo}
                            onChange={(e) => setEditData({...editData, sponsorshipInfo: e.target.value})}
                            style={{...editInputStyle, width: '100%', marginTop: '8px', textAlign: 'left'}}
                            placeholder="Your sponsorship info..."
                        />
                    ) : (
                        <span>{editData.sponsorshipInfo || sponsorshipInfo || 'Add your sponsorship details...'}</span>
                    )}
                </div>

                {isEditing && (
                    <div style={contactEditStyle}>
                        <strong>Contact Email: </strong>
                        <input
                            type="email"
                            value={editData.email}
                            onChange={(e) => setEditData({...editData, email: e.target.value})}
                            style={{...editInputStyle, width: '100%', marginTop: '8px', textAlign: 'left'}}
                            placeholder="your@email.com"
                        />
                    </div>
                )}
            </div>

            {/* Questions Section */}
            <div style={questionsSectionStyle}>
                <h2 style={sectionTitleStyle}>Questions?</h2>
                <p style={questionsTextStyle}>
                    Have questions or feedback? Feel free to reach out at{' '}
                    <a href={`mailto:${editData.email || 'contact@example.com'}`} style={linkStyle}>
                        {editData.email || 'contact@example.com'}
                    </a>
                </p>
            </div>

            {/* AI Chat Widget */}
            {isChatOpen && (
                <div style={chatModalStyle}>
                    {/* Chat Header */}
                    <div style={chatHeaderStyle}>
                        <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                            <div style={chatAvatarStyle}>
                                {profileImage ? (
                                    <img src={profileImage} alt="" style={{width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover'}} />
                                ) : (
                                    <svg width="20" height="20" viewBox="0 0 32 32">
                                        <rect width="32" height="32" rx="16" fill="#667EEA"/>
                                        <circle cx="16" cy="12" r="4" fill="#FFFFFF" opacity="0.9"/>
                                        <path d="M16 18c-4 0-7 2-7 4.5v1.5h14v-1.5c0-2.5-3-4.5-7-4.5z" fill="#FFFFFF" opacity="0.9"/>
                                    </svg>
                                )}
                            </div>
                            <span style={{fontWeight: 600, color: '#1f2937', fontSize: '15px'}}>Ask {firstName}'s AI</span>
                        </div>
                        <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                            <button style={chatHeaderButtonStyle} title="Options">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                    <circle cx="12" cy="5" r="2"/>
                                    <circle cx="12" cy="12" r="2"/>
                                    <circle cx="12" cy="19" r="2"/>
                                </svg>
                            </button>
                            <button style={chatHeaderButtonStyle} onClick={() => setIsChatOpen(false)} title="Close">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M18 6L6 18M6 6l12 12"/>
                                </svg>
                            </button>
                        </div>
                    </div>

                    {/* Chat Messages */}
                    <div style={chatMessagesStyle}>
                        {chatMessages.length === 0 && (
                            <div style={chatWelcomeStyle}>
                                <p style={{margin: 0, color: '#6b7280', fontSize: '14px'}}>
                                    Ask me anything about {firstName}'s articles and content!
                                </p>
                            </div>
                        )}

                        {chatMessages.map((msg, idx) => (
                            <div key={idx} style={chatMessageRowStyle(msg.role)}>
                                {msg.role === 'assistant' && (
                                    <div style={chatMessageAvatarStyle}>
                                        {profileImage ? (
                                            <img src={profileImage} alt="" style={{width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover'}} />
                                        ) : (
                                            <svg width="16" height="16" viewBox="0 0 32 32">
                                                <rect width="32" height="32" rx="16" fill="#667EEA"/>
                                                <circle cx="16" cy="12" r="4" fill="#FFFFFF"/>
                                                <path d="M16 18c-4 0-7 2-7 4.5v1.5h14v-1.5c0-2.5-3-4.5-7-4.5z" fill="#FFFFFF"/>
                                            </svg>
                                        )}
                                    </div>
                                )}
                                <div style={chatMessageBubbleStyle(msg.role)}>
                                    <p style={{margin: 0, fontSize: '14px', lineHeight: 1.5}}>{msg.content}</p>
                                    {msg.articleLink && (
                                        <a
                                            href={msg.articleLink.url}
                                            style={chatArticleLinkStyle}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            [Read the full newsletter here →]
                                        </a>
                                    )}
                                </div>
                            </div>
                        ))}

                        {chatMessages.length > 0 && (
                            <div style={chatTimestampStyle}>
                                {chatMessages[chatMessages.length - 1].role === 'assistant' ? 'AI bot' : 'Visitor'} {formatTime(chatMessages[chatMessages.length - 1].timestamp)}
                            </div>
                        )}

                        {isAiTyping && (
                            <div style={chatMessageRowStyle('assistant')}>
                                <div style={chatMessageAvatarStyle}>
                                    <svg width="16" height="16" viewBox="0 0 32 32">
                                        <rect width="32" height="32" rx="16" fill="#667EEA"/>
                                        <circle cx="16" cy="12" r="4" fill="#FFFFFF"/>
                                        <path d="M16 18c-4 0-7 2-7 4.5v1.5h14v-1.5c0-2.5-3-4.5-7-4.5z" fill="#FFFFFF"/>
                                    </svg>
                                </div>
                                <div style={chatMessageBubbleStyle('assistant')}>
                                    <div style={typingIndicatorStyle}>
                                        <span style={typingDotStyle(0)}>●</span>
                                        <span style={typingDotStyle(1)}>●</span>
                                        <span style={typingDotStyle(2)}>●</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div ref={chatEndRef} />
                    </div>

                    {/* Chat Input */}
                    <form onSubmit={handleSendMessage} style={chatInputFormStyle}>
                        <input
                            type="text"
                            value={chatInput}
                            onChange={(e) => setChatInput(e.target.value)}
                            placeholder="Type a message..."
                            style={chatInputFieldStyle}
                            disabled={isAiTyping}
                        />
                        <button
                            type="submit"
                            style={chatSendButtonStyle(!chatInput.trim() || isAiTyping)}
                            disabled={!chatInput.trim() || isAiTyping}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/>
                            </svg>
                        </button>
                    </form>
                </div>
            )}

            {/* Floating AI Chat Button */}
            {!isChatOpen && (
                <button
                    onClick={() => setIsChatOpen(true)}
                    style={floatingChatButtonStyle}
                    title={`Ask ${firstName}'s AI`}
                >
                    <svg width="28" height="28" viewBox="0 0 32 32">
                        <defs>
                            <linearGradient id="chat-btn-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" style={{stopColor: '#667EEA'}} />
                                <stop offset="100%" style={{stopColor: '#764BA2'}} />
                            </linearGradient>
                        </defs>
                        <rect width="32" height="32" rx="16" fill="url(#chat-btn-grad)"/>
                        <path d="M16 8c-4.4 0-8 3-8 6.7 0 2.2 1.3 4.2 3.4 5.4l-.9 2.9 3.5-1.8c.6.1 1.3.2 2 .2 4.4 0 8-3 8-6.7S20.4 8 16 8z" fill="#FFFFFF" opacity="0.9"/>
                    </svg>
                </button>
            )}
        </div>
    );
}

// Demo wrapper with sample data
export function ProfilePageDemo() {
    const [profileData, setProfileData] = useState(() => {
        const saved = localStorage.getItem('koenig-profile-data');
        if (saved) {
            return JSON.parse(saved);
        }
        return {
            name: 'Alex (The Silly Point) - Decoding cricket strategy one ball at time.',
            tagline: '',
            currentStatus: '**Current Status:** Covering the Ashes Test (Day 3). Writing a deep dive on Stokes\' captaincy.',
            faqItems: [
                {
                    question: 'Do you cover IPL?',
                    answer: 'Yes, I cover the IPL, but only from a tactical view, not gossip.'
                }
            ],
            sponsorshipInfo: 'I charge $500 per slot. My audience is 80% UK/India. Email me at alex@cricket.com.',
            email: 'alex@cricket.com'
        };
    });

    return (
        <div style={{
            minHeight: '100vh',
            background: '#f5f5f5'
        }}>
            <ProfilePage
                {...profileData}
                editable={true}
                onSubscribe={(email) => {
                    console.log('Subscribed:', email);
                    alert(`Thank you for subscribing with ${email}!`);
                }}
                onUpdate={(data) => {
                    setProfileData({...profileData, ...data});
                }}
            />
        </div>
    );
}

// Styles
const containerStyle = {
    maxWidth: '680px',
    margin: '0 auto',
    padding: '0 20px 40px 20px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    position: 'relative'
};

const navBarStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 0',
    marginBottom: '24px',
    borderBottom: '1px solid #eee'
};

const logoStyle = {
    display: 'flex',
    alignItems: 'center'
};

const navButtonsStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
};

const navButtonStyle = {
    padding: '8px 16px',
    background: 'transparent',
    border: '1px solid #ddd',
    borderRadius: '20px',
    fontSize: '14px',
    color: '#1a1a1a',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
};

const signUpButtonStyle = {
    padding: '8px 16px',
    background: '#FF6719',
    border: 'none',
    borderRadius: '20px',
    fontSize: '14px',
    color: 'white',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s ease'
};

const editButtonStyle = {
    padding: '8px 16px',
    background: '#667EEA',
    border: 'none',
    borderRadius: '20px',
    fontSize: '14px',
    color: 'white',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s ease'
};

const editButtonLinkStyle = {
    padding: '8px 16px',
    background: '#667EEA',
    border: 'none',
    borderRadius: '20px',
    fontSize: '14px',
    color: 'white',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    textDecoration: 'none',
    display: 'inline-block'
};

const headerSectionStyle = {
    textAlign: 'center',
    marginBottom: '32px'
};

const profileImageContainerStyle = {
    marginBottom: '24px'
};

const profileImageStyle = {
    width: '96px',
    height: '96px',
    borderRadius: '50%',
    objectFit: 'cover',
    border: '3px solid #fff',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
};

const defaultAvatarStyle = {
    width: '96px',
    height: '96px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #667EEA 0%, #764BA2 100%)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
};

const nameStyle = {
    fontSize: '28px',
    fontWeight: 700,
    color: '#1a1a1a',
    margin: '0 0 8px 0',
    lineHeight: 1.3
};

const taglineStyle = {
    fontSize: '16px',
    color: '#666',
    margin: 0
};

const editInputStyle = {
    width: '100%',
    padding: '12px 16px',
    border: '2px solid #667EEA',
    borderRadius: '8px',
    fontSize: '16px',
    outline: 'none',
    textAlign: 'center'
};

const subscriptionBoxStyle = {
    background: '#fff',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '24px',
    border: '1px solid #e5e5e5'
};

const brandingStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '16px'
};

const subscriptionFormStyle = {
    display: 'flex',
    gap: '12px'
};

const emailInputStyle = {
    flex: 1,
    padding: '12px 16px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '14px',
    outline: 'none',
    transition: 'border-color 0.2s ease'
};

const subscribeButtonStyle = (subscribed) => ({
    padding: '12px 24px',
    background: subscribed ? '#22c55e' : '#FF6719',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: subscribed ? 'default' : 'pointer',
    transition: 'all 0.2s ease',
    whiteSpace: 'nowrap'
});

const statusBoxStyle = {
    background: '#e8f5e9',
    borderRadius: '8px',
    padding: '16px 20px',
    marginBottom: '24px',
    fontSize: '15px',
    color: '#1a1a1a',
    lineHeight: 1.5,
    border: '1px solid #c8e6c9',
    display: 'flex',
    alignItems: 'flex-start'
};

const faqSectionStyle = {
    marginTop: '32px',
    marginBottom: '32px'
};

const sectionTitleStyle = {
    fontSize: '22px',
    fontWeight: 700,
    color: '#1a1a1a',
    margin: '0 0 20px 0'
};

const faqItemStyle = {
    marginBottom: '20px'
};

const faqQuestionStyle = {
    fontSize: '15px',
    color: '#1a1a1a',
    margin: '0 0 8px 0',
    lineHeight: 1.5
};

const faqAnswerStyle = {
    fontSize: '15px',
    color: '#666',
    margin: 0,
    lineHeight: 1.5
};

const sponsorshipStyle = {
    fontSize: '15px',
    color: '#1a1a1a',
    lineHeight: 1.5,
    marginTop: '16px'
};

const contactEditStyle = {
    marginTop: '16px',
    fontSize: '15px',
    color: '#1a1a1a'
};

const questionsSectionStyle = {
    marginTop: '32px',
    paddingTop: '24px',
    borderTop: '1px solid #eee'
};

const questionsTextStyle = {
    fontSize: '15px',
    color: '#666',
    lineHeight: 1.6
};

const linkStyle = {
    color: '#667EEA',
    textDecoration: 'none'
};

// AI Chat Styles
const floatingChatButtonStyle = {
    position: 'fixed',
    bottom: '24px',
    right: '24px',
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    border: 'none',
    cursor: 'pointer',
    boxShadow: '0 4px 16px rgba(102, 126, 234, 0.4)',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'transparent',
    padding: 0,
    transition: 'transform 0.2s ease, box-shadow 0.2s ease'
};

const chatModalStyle = {
    position: 'fixed',
    bottom: '24px',
    right: '24px',
    width: '380px',
    maxHeight: '520px',
    backgroundColor: '#fff',
    borderRadius: '16px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
    zIndex: 1001,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
};

const chatHeaderStyle = {
    padding: '16px 20px',
    background: '#fff',
    borderBottom: '1px solid #f0f0f0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
};

const chatAvatarStyle = {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f0f0f0'
};

const chatHeaderButtonStyle = {
    background: 'transparent',
    border: 'none',
    color: '#9ca3af',
    padding: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
};

const chatMessagesStyle = {
    flex: 1,
    overflowY: 'auto',
    padding: '16px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    background: '#fafafa',
    maxHeight: '320px'
};

const chatWelcomeStyle = {
    textAlign: 'center',
    padding: '20px'
};

const chatMessageRowStyle = (role) => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    justifyContent: role === 'user' ? 'flex-end' : 'flex-start'
});

const chatMessageAvatarStyle = {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    overflow: 'hidden',
    flexShrink: 0
};

const chatMessageBubbleStyle = (role) => ({
    maxWidth: '75%',
    padding: '10px 14px',
    borderRadius: role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
    background: role === 'user' ? '#1a1a1a' : '#fff',
    color: role === 'user' ? '#fff' : '#1a1a1a',
    border: role === 'user' ? 'none' : '1px solid #e5e5e5',
    fontSize: '14px',
    lineHeight: 1.5
});

const chatArticleLinkStyle = {
    display: 'block',
    marginTop: '8px',
    color: '#3b82f6',
    textDecoration: 'none',
    fontSize: '13px'
};

const chatTimestampStyle = {
    textAlign: 'center',
    fontSize: '11px',
    color: '#9ca3af',
    marginTop: '4px'
};

const typingIndicatorStyle = {
    display: 'flex',
    gap: '4px',
    alignItems: 'center'
};

const typingDotStyle = (delay) => ({
    color: '#9ca3af',
    animation: `pulse 1.4s ease-in-out ${delay * 0.2}s infinite`
});

const chatInputFormStyle = {
    padding: '12px 16px',
    borderTop: '1px solid #f0f0f0',
    display: 'flex',
    gap: '8px',
    background: '#fff'
};

const chatInputFieldStyle = {
    flex: 1,
    padding: '10px 14px',
    border: '1px solid #e5e5e5',
    borderRadius: '20px',
    fontSize: '14px',
    outline: 'none'
};

const chatSendButtonStyle = (disabled) => ({
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    border: 'none',
    background: disabled ? '#e5e5e5' : '#667EEA',
    color: '#fff',
    cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    opacity: disabled ? 0.5 : 1,
    transition: 'all 0.2s ease'
});

export default ProfilePage;
